// Users: every account across the platform. Search and kind filter live in
// the URL (?q=&kind=&page=). Rows open /users/:id.
//
// "Invite super admin" (registration plan S2): POST /platform/super-admin-
// invites { email } after a confirm in the dialog; the link opens on Ops and
// works for 48 hours. The pending super-admin invites sit above the table.

import { useMemo, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import type { InviteSendResult, PlatformUser, UserKind } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import { Button, Card, DataTable, Dialog, EmailText, EmptyState, ErrorState, FilterChips, FormField, InlineAlert, Pagination, PlusIcon, RelativeTime, SearchInput, SkeletonRows, TextInput, type Column } from '@/components/ui'
import { useDocumentTitle } from '@/lib/hooks'
import { UserRoleBadges, UserStatusBadge } from './users/UserBadges'
import { invalidateQueries, useQuery } from '@/lib/query'
import { InviteResult, PendingInvitesCard } from './invites/StaffInvites'

const PAGE_SIZE = 25

type KindFilter = UserKind | 'all'

const KINDS: Array<{ value: KindFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'super_admin', label: 'Super-admins' },
  { value: 'league_admin', label: 'League admins' },
  { value: 'orphaned', label: 'No league' },
  { value: 'inactive', label: 'Deactivated' },
]

function readParams(p: URLSearchParams) {
  const kind = KINDS.find((k) => k.value === p.get('kind'))?.value ?? 'all'
  const page = Number(p.get('page'))
  return { q: p.get('q') ?? '', kind, page: Number.isInteger(page) && page > 0 ? page : 1 }
}

function Person({ user }: { user: PlatformUser }) {
  return (
    <span className="grid min-w-0 gap-0.5">
      <span className="truncate font-semibold">{user.name || user.email}</span>
      {user.name ? (
        <span className="font-mono text-xs font-medium text-muted [overflow-wrap:anywhere]">
          <EmailText email={user.email} />
        </span>
      ) : null}
    </span>
  )
}

function Leagues({ user }: { user: PlatformUser }) {
  if (!user.memberships.length) return <span className="text-subtle">No league</span>
  const [first, ...rest] = user.memberships
  return (
    <span className="text-[13px]" title={user.memberships.map((m) => `${m.league_name} (${m.role})`).join(', ')}>
      {first.league_name}
      {rest.length ? <span className="text-muted"> +{rest.length} more</span> : null}
    </span>
  )
}

const COLUMNS: Column<PlatformUser>[] = [
  { id: 'person', header: 'Person', primary: true, cell: (u) => <Person user={u} />, className: 'min-w-[15rem] max-w-[18rem]' },
  { id: 'role', header: 'Role', cell: (u) => <UserRoleBadges user={u} /> },
  { id: 'leagues', header: 'Leagues', cell: (u) => <Leagues user={u} />, className: 'min-w-[9rem]' },
  { id: 'status', header: 'Status', cell: (u) => <UserStatusBadge active={u.is_active} /> },
  { id: 'login', header: 'Last login', cell: (u) => <RelativeTime value={u.last_login_at} staleAfterDays={90} /> },
]

export default function UsersPage() {
  useDocumentTitle('Users')
  const [searchParams, setSearchParams] = useSearchParams()
  const { q, kind, page } = readParams(searchParams)

  const update = (patch: Record<string, string | number | undefined>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '' || (k === 'kind' && v === 'all') || (k === 'page' && v === 1)) next.delete(k)
          else next.set(k, String(v))
        }
        if (!('page' in patch)) next.delete('page')
        return next
      },
      { replace: true },
    )

  const params = useMemo(() => ({ q, kind: kind === 'all' ? undefined : kind, page, page_size: PAGE_SIZE }), [q, kind, page])
  const { data, error, status, isFetching, refetch } = useQuery(['users', 'list', params], (signal) => api.users.list(params, signal), { keepPrevious: true })
  const counts = data?.kind_counts
  const rows = data?.users ?? []
  const filtered = !!q || kind !== 'all'
  const [inviting, setInviting] = useState(false)

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        title="Users"
        description={counts ? `${counts.all} ${counts.all === 1 ? 'account' : 'accounts'}${q ? ` matching “${q}”` : ''}: super-admins, league admins and accounts with no league.` : 'Super-admins and league admins across every tenant.'}
        actions={
          <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setInviting(true)}>
            Invite super admin
          </Button>
        }
      />
      <InviteSuperAdminDialog open={inviting} onClose={() => setInviting(false)} />
      <PendingInvitesCard title="Pending super-admin invites" queryKey={['users', 'super-admin-invites']} load={(signal) => api.invites.listSuperAdmin(signal)} />
      <div className="grid gap-3">
        <SearchInput label="Search users" placeholder="Search email or name" value={q} onChange={(v) => update({ q: v })} className="sm:max-w-sm" />
        <FilterChips
          label="Filter by kind"
          options={KINDS.map((k) => ({ value: k.value, label: k.label, count: counts ? counts[k.value] : undefined }))}
          value={kind}
          onChange={(v) => update({ kind: v })}
        />
      </div>
      <Card flush className="overflow-hidden">
        {status === 'loading' ? (
          <SkeletonRows rows={6} label="Loading users" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load users" onRetry={refetch} />
        ) : rows.length === 0 ? (
          // ?page= past the end of a non-empty list (an old link, or the list shrank).
          data && data.total > 0 ? (
            <EmptyState
              title="No results on this page"
              description={`Page ${page} is past the end. ${data.total} ${data.total === 1 ? 'account' : 'accounts'}${filtered ? ' match' : ' in all'}.`}
              action={
                <Button size="sm" onClick={() => update({ page: 1 })}>
                  Go to page 1
                </Button>
              }
            />
          ) : filtered ? (
            <EmptyState
              title="No users match"
              description="Try another search or filter."
              action={
                <Button size="sm" onClick={() => update({ q: undefined, kind: undefined })}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState title="No users yet" description="Accounts appear here when a tenant gets an admin." />
          )
        ) : (
          <>
            {status === 'error' ? (
              <div role="alert" className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-bad-soft/40 px-4 py-2 text-xs text-bad">
                Couldn't refresh. Showing the last results.
                <Button size="sm" variant="ghost" onClick={refetch}>
                  Retry
                </Button>
              </div>
            ) : null}
            <DataTable caption="Users" rows={rows} columns={COLUMNS} rowKey={(u) => u.id} rowHref={(u) => `/users/${u.id}`} busy={isFetching && status === 'success'} />
            <div className="border-t border-line px-4 py-3">
              <Pagination page={data?.page ?? page} pageSize={data?.page_size ?? PAGE_SIZE} total={data?.total ?? 0} noun="users" onPageChange={(p) => update({ page: p })} />
            </div>
          </>
        )}
      </Card>
    </div>
  )
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function InviteSuperAdminDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [result, setResult] = useState<InviteSendResult | null>(null)
  const [problem, setProblem] = useState<{ text: string; inviteId?: number; inviteEmail?: string } | null>(null)
  // "Resend it" emails someone, so it asks first, like every other send: the
  // dialog switches to a confirm step for the pending invite.
  const [confirmResend, setConfirmResend] = useState<{ inviteId: number; email: string } | null>(null)

  const close = () => {
    if (pending) return
    setEmail('')
    setEmailError(null)
    setResult(null)
    setProblem(null)
    setConfirmResend(null)
    onClose()
  }

  const done = (r: InviteSendResult) => {
    setResult(r)
    setProblem(null)
    invalidateQueries('users')
  }

  const submit = async (e?: FormEvent) => {
    e?.preventDefault()
    const clean = email.trim().toLowerCase()
    if (!EMAIL_RE.test(clean)) {
      setEmailError('Enter a valid email address.')
      return
    }
    setEmailError(null)
    setProblem(null)
    setPending(true)
    try {
      done(await api.invites.inviteSuperAdmin(clean))
    } catch (err) {
      if (isApiError(err) && err.field === 'email') setEmailError(err.message)
      else if (isApiError(err) && err.code === 'invite_pending') {
        const id = Number(err.details?.invite_id)
        setProblem({ text: err.message, inviteId: Number.isInteger(id) && id > 0 ? id : undefined, inviteEmail: clean })
      } else setProblem({ text: errorMessage(err) })
    } finally {
      setPending(false)
    }
  }

  const resend = async (inviteId: number) => {
    setPending(true)
    try {
      done(await api.invites.resendInvite(inviteId))
    } catch (err) {
      setProblem({ text: errorMessage(err) })
    } finally {
      setConfirmResend(null)
      setPending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      dismissible={!pending}
      title="Invite super admin"
      description="They get a link to become a ScoreLM super admin: full access to every league, billing and the other super admins. Every super admin is notified when they join."
      footer={
        result ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : confirmResend ? (
          <>
            <Button onClick={() => setConfirmResend(null)} disabled={pending}>
              Cancel
            </Button>
            <Button variant="primary" loading={pending} onClick={() => void resend(confirmResend.inviteId)}>
              Resend invite
            </Button>
          </>
        ) : (
          <>
            <Button onClick={close} disabled={pending}>
              Cancel
            </Button>
            <Button variant="primary" loading={pending} onClick={() => void submit()}>
              Send invite
            </Button>
          </>
        )
      }
    >
      {result ? (
        <InviteResult result={result} className="break-words" />
      ) : confirmResend ? (
        <div className="grid gap-1 break-words">
          <p className="text-sm font-semibold text-ink">Resend the invite to {confirmResend.email}?</p>
          <p className="text-sm text-muted">They get the same link again. It still expires when it was going to.</p>
        </div>
      ) : (
        <form noValidate onSubmit={(e) => void submit(e)} className="grid gap-4">
          <FormField label="Email" required error={emailError}>
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                type="email"
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-describedby={describedBy}
                invalid={invalid}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={pending}
              />
            )}
          </FormField>
          {problem ? (
            <InlineAlert
              tone="bad"
              className="break-words"
              action={
                problem.inviteId ? (
                  <Button size="sm" onClick={() => setConfirmResend({ inviteId: problem.inviteId!, email: problem.inviteEmail ?? email.trim().toLowerCase() })}>
                    Resend it
                  </Button>
                ) : undefined
              }
            >
              {problem.text}
            </InlineAlert>
          ) : null}
        </form>
      )}
    </Dialog>
  )
}
